import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LI } from '../../../AbstractElements';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Badge } from 'reactstrap';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import moment from 'moment';

export default function Notification() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<any[]>([]);
    const [notificationCount, setNotificationCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const fetchNotifications = useCallback(async () => {
        const rawServerUrl = localStorage.getItem("serverUrl") || "https://sfa.uat.nitiapp.com/";
        const serverUrl = rawServerUrl.endsWith("/") ? rawServerUrl : `${rawServerUrl}/`;
        const endpoint = (API.getNotification || "api/notifications").replace(/^\//, "");
        const url = `${serverUrl}${endpoint}`;

        setLoading(true);
        try {
            const response = await getData(url, true, { sort_by: 0 });
            if (response?.status === 200 || response?.data?.statusCode === 200) {
                const resData = response.data?.data || response.data;
                const actions: any[] = resData?.Actions?.actions || [];
                const emps: any[] = resData?.Actions?.emps || [];

                const mapped = actions.map((item: any) => {
                    const matchedEmp = emps.find(
                        (emp: any) => String(emp.EmployeeId) === String(item.WfOriginEmployee)
                    );
                    return {
                        ...item,
                        employeeData: matchedEmp ? [matchedEmp] : [],
                    };
                });

                setNotifications(mapped);
                setNotificationCount(mapped.length);
            } else {
                setNotifications([]);
                setNotificationCount(0);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
            setNotifications([]);
            setNotificationCount(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        // Periodic background poll every 60 seconds
        const timer = setInterval(() => {
            fetchNotifications();
        }, 60000);
        return () => clearInterval(timer);
    }, [fetchNotifications]);

    const handleNotificationClick = (item: any) => {
        setModalOpen(false);
        const entity = (item.WfEntityName || "").toLowerCase();
        const route = (item.WfRoute || "").toLowerCase();

        // Map entity/route to corresponding approval tab in ApprovalTabs:
        // Tab 1: MTP Approval
        // Tab 2: Leave Approval
        // Tab 3: Expense Approval
        // Tab 4: POD Approval
        // Tab 5: POD Summary
        // Tab 6: Support Approval
        // Tab 7: Closing Stock Approval
        // Tab 8: Holiday Approvals
        // Tab 9: GeoFencing Approvals
        let activeTab = "1";

        if (entity.includes("leave") || route.includes("leave")) {
            activeTab = "2";
        } else if (entity.includes("mtp") || route.includes("mtp") || entity.includes("tour") || route.includes("tour")) {
            activeTab = "1";
        } else if (entity.includes("expense") || route.includes("expense")) {
            activeTab = "3";
        } else if (entity.includes("podsummary") || route.includes("podsummary")) {
            activeTab = "5";
        } else if (entity.includes("pod") || route.includes("pod")) {
            activeTab = "4";
        } else if (entity.includes("support") || route.includes("support")) {
            activeTab = "6";
        } else if (entity.includes("closingstock") || entity.includes("stock") || route.includes("closingstock")) {
            activeTab = "7";
        } else if (entity.includes("holiday") || route.includes("holiday")) {
            activeTab = "8";
        } else if (entity.includes("geofencing") || entity.includes("geo") || route.includes("geofencing")) {
            activeTab = "9";
        } else {
            activeTab = "1";
        }

        navigate("/Approvals", {
            state: {
                activeTab,
                wfReqId: item.WfReqId,
                docPk: item.WfDocPk,
                originEmployee: item.WfOriginEmployee,
                notification: item,
            },
        });
    };

    const getEntityBadgeColor = (entity: string) => {
        const e = (entity || "").toLowerCase();
        if (e.includes("leave")) return { bg: "#eff6ff", text: "#1d4ed8", border: "#3b82f6" };
        if (e.includes("event")) return { bg: "#f5f3ff", text: "#6d28d9", border: "#8b5cf6" };
        if (e.includes("expense")) return { bg: "#ecfdf5", text: "#047857", border: "#10b981" };
        if (e.includes("mtp") || e.includes("tour")) return { bg: "#fff7ed", text: "#c2410c", border: "#f97316" };
        if (e.includes("pod")) return { bg: "#eef2ff", text: "#4338ca", border: "#6366f1" };
        if (e.includes("support")) return { bg: "#ecfeff", text: "#0e7490", border: "#06b6d4" };
        if (e.includes("stock")) return { bg: "#fff1f2", text: "#be123c", border: "#f43f5e" };
        if (e.includes("holiday")) return { bg: "#fefce8", text: "#a16207", border: "#eab308" };
        if (e.includes("geo")) return { bg: "#f0f9ff", text: "#0369a1", border: "#0284c7" };
        return { bg: "#f8fafc", text: "#334155", border: "#64748b" };
    };

    return (
        <LI className="position-relative list-unstyled">
            {/* Header Notification Icon with Badge */}
            <div
                className="notification-box position-relative d-flex align-items-center justify-content-center"
                style={{
                    cursor: "pointer",
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    backgroundColor: modalOpen ? "#e2e8f0" : "transparent",
                    transition: "all 0.2s ease",
                }}
                onClick={() => {
                    fetchNotifications();
                    setModalOpen(true);
                }}
                title="Notifications"
            >
                <FeatherIconCom iconName="Bell" size={20} className="text-dark" />
                {notificationCount > 0 && (
                    <span
                        className="badge rounded-pill bg-danger position-absolute d-flex align-items-center justify-content-center"
                        style={{
                            top: "2px",
                            right: "2px",
                            fontSize: "10px",
                            fontWeight: "bold",
                            minWidth: "18px",
                            height: "18px",
                            padding: "0 4px",
                            border: "1.5px solid #ffffff",
                            lineHeight: "1",
                            boxShadow: "0 2px 4px rgba(220, 53, 69, 0.4)",
                        }}
                    >
                        {notificationCount > 99 ? "99+" : notificationCount}
                    </span>
                )}
            </div>

            {/* Notification Modal */}
            <Modal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                centered
                style={{ maxWidth: "620px", width: "95%" }}
                contentClassName="border-0 shadow-lg rounded-4 overflow-hidden bg-white"
            >
                {/* Modal Header using native ModalHeader toggle for standard close button */}
                <ModalHeader
                    toggle={() => setModalOpen(!modalOpen)}
                    className="border-bottom py-3 px-4 bg-white"
                >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 w-100 pe-3">
                        <div className="d-flex align-items-center gap-2">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                style={{
                                    width: "36px",
                                    height: "36px",
                                    backgroundColor: "#2563eb",
                                    boxShadow: "0 3px 8px rgba(37, 99, 235, 0.25)",
                                    flexShrink: 0,
                                }}
                            >
                                <FeatherIconCom iconName="Bell" size={18} style={{ stroke: "#ffffff" }} />
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold text-dark fs-5">Notifications</span>
                                    {notificationCount > 0 && (
                                        <Badge
                                            color="danger"
                                            pill
                                            style={{ fontSize: "0.75rem", fontWeight: "700" }}
                                        >
                                            {notificationCount}
                                        </Badge>
                                    )}
                                </div>
                                <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                    {notificationCount > 0
                                        ? `${notificationCount} pending approval${notificationCount > 1 ? "s" : ""}`
                                        : "All approvals caught up"}
                                </div>
                            </div>
                        </div>

                        {/* Refresh button next to close toggle */}
                        <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center gap-1 px-3 py-1 ms-auto me-2 fw-semibold"
                            title="Refresh notifications"
                            onClick={(e) => {
                                e.stopPropagation();
                                fetchNotifications();
                            }}
                            disabled={loading}
                            style={{
                                borderRadius: "8px",
                                fontSize: "0.82rem",
                                backgroundColor: "#f8fafc",
                                border: "1px solid #cbd5e1",
                                color: "#334155",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#e2e8f0";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#f8fafc";
                            }}
                        >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#334155"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={loading ? "spin" : ""}
                                style={{ display: "block" }}
                            >
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <polyline points="1 20 1 14 7 14"></polyline>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                            </svg>
                            <span>Refresh</span>
                        </button>
                    </div>
                </ModalHeader>

                {/* Modal Body */}
                <ModalBody
                    className="p-3"
                    style={{
                        maxHeight: "480px",
                        overflowY: "auto",
                        backgroundColor: "#f8fafc",
                    }}
                >
                    {loading && notifications.length === 0 ? (
                        <div className="text-center py-5">
                            <Spinner size="md" color="primary" />
                            <div className="small text-muted mt-2 fw-semibold">Loading notifications...</div>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="text-center py-5 px-4 text-muted bg-white rounded-3 border">
                            <div
                                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                style={{ width: "60px", height: "60px", backgroundColor: "#f1f5f9", color: "#64748b" }}
                            >
                                <FeatherIconCom iconName="BellOff" size={28} />
                            </div>
                            <h6 className="fw-bold text-dark mb-1">No Pending Notifications</h6>
                            <p className="small text-muted m-0">
                                You do not have any pending approvals or workflow actions right now.
                            </p>
                        </div>
                    ) : (
                        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
                            {notifications.map((item: any, idx: number) => {
                                const emp = item.employeeData?.[0];
                                const empName = emp
                                    ? `${emp.FirstName || ""} ${emp.LastName || ""}`.trim()
                                    : `Employee #${item.WfOriginEmployee || ""}`;
                                const entityName = item.WfEntityName || "Approval";
                                const desc = item.WfDesc || "Pending Approval";
                                const colors = getEntityBadgeColor(entityName);
                                const timeAgo = item.CreatedAt ? moment(item.CreatedAt).fromNow() : "";
                                const createdDate = item.CreatedAt
                                    ? moment(item.CreatedAt).format("DD MMM YYYY, hh:mm A")
                                    : "";

                                return (
                                    <div
                                        key={item.WfReqId || idx}
                                        onClick={() => handleNotificationClick(item)}
                                        style={{
                                            width: "100%",
                                            display: "block",
                                            boxSizing: "border-box",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "10px",
                                            border: "1px solid #e2e8f0",
                                            borderLeft: `5px solid ${colors.border}`,
                                            padding: "16px",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0, 0, 0, 0.08)";
                                            e.currentTarget.style.transform = "translateY(-1px)";
                                            e.currentTarget.style.borderColor = colors.border;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.05)";
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                        }}
                                    >
                                        {/* Top Row: Entity Tag + Status Pill + Time Ago */}
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2 w-100">
                                            <div className="d-flex align-items-center gap-2">
                                                <span
                                                    className="d-inline-flex align-items-center gap-1 px-2 py-1"
                                                    style={{
                                                        backgroundColor: colors.bg,
                                                        color: colors.text,
                                                        border: `1px solid ${colors.border}`,
                                                        fontSize: "0.78rem",
                                                        fontWeight: "700",
                                                        borderRadius: "6px",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            width: "6px",
                                                            height: "6px",
                                                            borderRadius: "50%",
                                                            backgroundColor: colors.border,
                                                            display: "inline-block",
                                                        }}
                                                    ></span>
                                                    {entityName}
                                                </span>

                                                <span
                                                    className="px-2 py-1"
                                                    style={{
                                                        backgroundColor: "#fef3c7",
                                                        color: "#92400e",
                                                        fontSize: "0.74rem",
                                                        fontWeight: "700",
                                                        borderRadius: "6px",
                                                    }}
                                                >
                                                    Pending
                                                </span>
                                            </div>

                                            <span className="text-muted small fw-medium" style={{ fontSize: "0.78rem" }}>
                                                {timeAgo}
                                            </span>
                                        </div>

                                        {/* Middle Row: Description */}
                                        <div
                                            className="fw-bold mb-2 text-dark"
                                            style={{
                                                fontSize: "1rem",
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            {desc}
                                        </div>

                                        {/* Requester Row */}
                                        <div
                                            className="d-flex align-items-center gap-2 px-3 py-2 mb-3 rounded"
                                            style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}
                                        >
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                                style={{
                                                    width: "24px",
                                                    height: "24px",
                                                    backgroundColor: colors.border,
                                                    fontSize: "0.72rem",
                                                    fontWeight: "700",
                                                }}
                                            >
                                                {empName.charAt(0).toUpperCase() || "U"}
                                            </div>
                                            <div className="text-truncate" style={{ fontSize: "0.84rem" }}>
                                                <span className="text-muted">Requested by: </span>
                                                <strong className="text-dark ms-1">{empName}</strong>
                                            </div>
                                        </div>

                                        {/* Bottom Row: Timestamp + Action Indicator */}
                                        <div
                                            className="d-flex align-items-center justify-content-between pt-2 border-top w-100"
                                            style={{ borderColor: "#f1f5f9" }}
                                        >
                                            <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: "0.78rem" }}>
                                                <FeatherIconCom iconName="Clock" size={13} style={{ stroke: "#64748b" }} />
                                                <span>{createdDate}</span>
                                            </div>

                                            <div
                                                className="d-flex align-items-center gap-1 fw-bold ms-auto ps-3"
                                                style={{ color: colors.border, fontSize: "0.82rem" }}
                                            >
                                                <span>Review Approval</span>
                                                <FeatherIconCom iconName="ChevronRight" size={14} style={{ stroke: colors.border }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ModalBody>

                {/* Modal Footer */}
                <ModalFooter
                    className="d-flex justify-content-between align-items-center px-4 py-3 bg-white border-top"
                >
                    <button
                        type="button"
                        className="btn btn-primary fw-semibold px-3 py-2 border-0 d-flex align-items-center gap-2"
                        style={{
                            borderRadius: "8px",
                            fontSize: "0.86rem",
                            backgroundColor: "#2563eb",
                            boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)",
                        }}
                        onClick={() => {
                            setModalOpen(false);
                            navigate("/Approvals");
                        }}
                    >
                        <span>Go to Approvals Dashboard</span>
                        <FeatherIconCom iconName="ArrowRight" size={14} style={{ stroke: "#ffffff" }} />
                    </button>

                    <button
                        type="button"
                        className="btn fw-semibold px-4 py-2"
                        style={{
                            backgroundColor: "#f1f5f9",
                            color: "#334155",
                            border: "1px solid #cbd5e1",
                            borderRadius: "8px",
                            fontSize: "0.86rem",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#e2e8f0";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#f1f5f9";
                        }}
                        onClick={() => setModalOpen(false)}
                    >
                        Close
                    </button>
                </ModalFooter>
            </Modal>
        </LI>
    );
}
